/* eslint-disable array-callback-return */
import axios from 'axios';
import queryString from 'query-string';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { Alert } from '../../assets';
import { BASE_URL } from '../../constants/api-urls';
import {
  SPOTIFY_CODE,
  SPOTIFY_REFRESH_TOKEN,
  SPOTIFY_TOKEN,
  SPOTIFY_USER_INFO,
} from '../../constants/storage';
import { useLoginContext } from '../../context';
import { usePackageContext } from '../../context/PackageContext';
import {
  Auth0Logout,
  addItemToStorage,
  deleteItemFromStorage,
  getRefreshedToken,
} from '../../utils';
import {
  getShiftLandingPath,
  isOnboardingIntentFeatureEnabled,
  normalizeOnboardingIntent,
  OnboardingIntent,
  shouldShowIntentScreen,
} from '../../utils/onboardingIntent';
import { OnboardingIntentModal } from '../../components/OnboardingIntent';

import PricingModal from '../landingPage/PaymentModal';
import { LoaderPageStyle, ExpirePageStyle } from './ShiftPage.style';
import { handleWrongPlatformError } from '../../utils/wrongPlatform';
import { storeWalletPlan } from '../../utils/planType';
import { getCuratorAuthHeader } from '../../utils/curatorApiAuth';
import { persistAuth0GrantFromHash } from '../../utils/auth0Session';
import {
  FREE_PLAN_COMPLETION_PATH,
  shouldDowngradeToFreePlan,
} from '../../utils/subscriptionStatus';

const ShiftPage = () => {
  const proPlanMonthly = process.env.REACT_APP_STRIPE_PRO_MONTHLY;
  const proPlan = process.env.REACT_APP_STRIPE_PRO;
  const unlimitedPlanMonthly = process.env.REACT_APP_STRIPE_UNLIMITED_MONTHLY;
  const unlimitedPlan = process.env.REACT_APP_STRIPE_UNLIMITED;
  let ides = '';

  const access_token = queryString.parse(useLocation().hash.substring(1)); // Remove '#' from hash
  const user = JSON.parse(localStorage.getItem('userInfo'));

  const shiftSearchParams = new URLSearchParams(window.location.search);
  const plan = shiftSearchParams.get('plan');
  const planId = shiftSearchParams.get('planId');
  const planPaid = shiftSearchParams.get('planPaid');
  const defaultCurrency = shiftSearchParams.get('defaultCurrency');

  const { clearCache } = usePackageContext();
  const { dispatch } = useLoginContext();

  const [princingModalIsOpen, setPrincingModalIsOpen] = useState<boolean>(false);
  const [intentModalIsOpen, setIntentModalIsOpen] = useState<boolean>(false);
  const [intentLoading, setIntentLoading] = useState<boolean>(false);
  const [intentError, setIntentError] = useState<string | null>(null);
  const [message, setShowMessage] = useState('Loading');
  const [expireStatus, setExpireStatus] = useState<string>('');
  const [platformError, setPlatformError] = useState<string | null>(null);

  const getLatestSpotifyProfile = (profiles: any[]) => {
    if (!Array.isArray(profiles) || profiles.length === 0) return null;

    return [...profiles].sort((a, b) => {
      const aTime = new Date(a?.created_at || a?.updatedAt || 0).getTime();
      const bTime = new Date(b?.created_at || b?.updatedAt || 0).getTime();
      return bTime - aTime;
    })[0];
  };

  const hydrateSpotifyStateFromProfiles = async (profiles: any[]) => {
    if (!Array.isArray(profiles) || profiles.length === 0) {
      localStorage.setItem('hasSpotifyConnection', 'false');
      return;
    }

    // Only honor active profile id when it belongs to THIS Songplace user's profiles.
    // Stale ids from another account on the same browser must not be used.
    const activeId = localStorage.getItem('SPOTIFY_ACTIVE_PROFILE_ID');
    let selectedProfile =
      (activeId && profiles.find((p: any) => String(p?._id) === String(activeId))) ||
      getLatestSpotifyProfile(profiles);

    if (!selectedProfile) {
      localStorage.setItem('hasSpotifyConnection', 'false');
      return;
    }

    localStorage.setItem('SPOTIFY_ACTIVE_PROFILE_ID', String(selectedProfile._id));

    const imageValue =
      Array.isArray(selectedProfile?.image) && selectedProfile?.image?.[0]?.url
        ? selectedProfile.image[0].url
        : selectedProfile?.image || 'dummy';

    const profileIdentity = {
      profileId: String(selectedProfile._id),
      spotifyID: selectedProfile.spotifyID || null,
      username: selectedProfile.username,
      image: imageValue,
    };

    const requireReconnect = async (reason: string) => {
      const { clearSpotifySession } = await import('../../utils/spotifyAuth');
      // Always pin reconnect intent to the selected/current profile only.
      clearSpotifySession(profileIdentity, reason);
      dispatch({
        type: 'SPOTIFY_REAUTH_REQUIRED',
        payload: profileIdentity,
      });
    };

    // Server health: do not rehydrate dead refresh tokens as a healthy connection
    if (selectedProfile.spotifyNeedsReconnect === true) {
      await requireReconnect('login_hydrate_needs_reconnect');
      return;
    }

    if (!selectedProfile.refresh) {
      await requireReconnect('login_hydrate_missing_refresh');
      return;
    }

    let token = selectedProfile.token;
    let refreshToken = selectedProfile.refresh;
    try {
      const refreshResponse = await getRefreshedToken(selectedProfile.refresh, profileIdentity);
      token = refreshResponse?.data?.access_token;
      refreshToken = refreshResponse?.data?.refresh_token || selectedProfile.refresh;
      if (!token) {
        throw new Error('Spotify did not return an access token');
      }
    } catch (error: any) {
      if (error?.code === 'SPOTIFY_REAUTH_REQUIRED') {
        return;
      }
      // Preserve the existing session on a transient failure. It can retry through TokenManager later.
      console.error('Unable to validate Spotify profile during login:', error);
    }

    const data = {
      username: selectedProfile?.username,
      image: imageValue,
      code: selectedProfile?.code,
      refresh: refreshToken,
      profileId: selectedProfile?._id,
      spotifyID: selectedProfile?.spotifyID,
      spotify_user_id: selectedProfile?.spotifyID,
    };

    localStorage.setItem('spotifyUser', JSON.stringify(data, null, ' '));
    addItemToStorage(SPOTIFY_TOKEN, token);
    addItemToStorage(SPOTIFY_REFRESH_TOKEN, refreshToken);
    localStorage.setItem('hasSpotifyConnection', 'true');
    localStorage.removeItem('SPOTIFY_NEEDS_RECONNECT');

    dispatch({
      type: 'ADD_APP_TOKEN',
      payload: {
        token,
        refreshToken,
      },
    });
    dispatch({
      type: 'SPOTIFY_CONNECTED',
      payload: {
        token,
        refreshToken,
        profileId: String(selectedProfile._id),
      },
    });
  };

  const handleProfileFetchFailure = () => {
    // Preserve previously connected state when profile fetch is temporarily unavailable.
    const hasCachedSpotifyProfile = Boolean(localStorage.getItem('spotifyUser'));
    localStorage.setItem('hasSpotifyConnection', String(hasCachedSpotifyProfile));
  };

  const mergeUserInfo = (partial: Record<string, any>) => {
    try {
      const current = JSON.parse(localStorage.getItem('userInfo') || '{}');
      const next = { ...current, ...partial };
      localStorage.setItem('userInfo', JSON.stringify(next));
      return next;
    } catch {
      return partial;
    }
  };

  const redirectAfterAccessGranted = (userMetadata: any) => {
    const path = getShiftLandingPath({
      free: userMetadata?.free,
      hasActiveOrTrialingPlan: false,
      onboardingIntent: userMetadata?.onboardingIntent,
      campaignsOnboardingRequired: userMetadata?.campaignsOnboardingRequired,
    });
    window.location.replace(path);
  };

  const handleNoActivePackage = (userMetadata: any) => {
    const intent = normalizeOnboardingIntent(userMetadata?.onboardingIntent);

    // Already free → land (campaigns onboarding if still required)
    if (userMetadata?.free) {
      const config = {
        headers: {
          'Content-Type': 'application-json',
        },
      };
      axios
        .get(
          `${BASE_URL}/user/${JSON.parse(localStorage.getItem('userInfo') || '{}')?.userId}/data`,
          config
        )
        .then(async call => {
          await hydrateSpotifyStateFromProfiles(call?.data?.data || []);
          setTimeout(() => {
            redirectAfterAccessGranted(userMetadata);
          }, 1500);
        })
        .catch(() => {
          handleProfileFetchFailure();
          setTimeout(() => {
            redirectAfterAccessGranted(userMetadata);
          }, 1500);
        });
      return;
    }

    // Feature off → legacy pricing path
    if (!isOnboardingIntentFeatureEnabled()) {
      openPricingOrDeepLinkPlan();
      return;
    }

    // Intent not chosen yet → show A/B/C screen (first-time only)
    if (
      shouldShowIntentScreen({
        free: userMetadata?.free,
        hasActiveOrTrialingPlan: false,
        onboardingIntent: intent,
        expireStatus: '',
      })
    ) {
      setIntentModalIsOpen(true);
      return;
    }

    // Intent A already saved but free not applied (resume) → finish free path
    if (intent === 'submissions') {
      completeSubmissionsIntentPath();
      return;
    }

    // Intent B or C (or any locked intent needing plan) → pricing
    openPricingOrDeepLinkPlan();
  };

  const openPricingOrDeepLinkPlan = () => {
    if (plan === 'planChoosen' && planId) {
      if (planId === process.env.REACT_APP_STRIPE_PRO_MONTHLY) {
        PricingNext(planId, planPaid || '', 'month', defaultCurrency || 'usd');
      } else if (planId === process.env.REACT_APP_STRIPE_UNLIMITED_MONTHLY) {
        PricingNext(planId, planPaid || '', 'month', defaultCurrency || 'usd');
      } else if (planId === process.env.REACT_APP_STRIPE_PRO) {
        PricingNext(planId, planPaid || '', 'year', defaultCurrency || 'usd');
      } else if (planId === process.env.REACT_APP_STRIPE_UNLIMITED) {
        PricingNext(planId, planPaid || '', 'year', defaultCurrency || 'usd');
      } else {
        setPrincingModalIsOpen(true);
      }
    } else {
      setPrincingModalIsOpen(true);
    }
  };

  const completeSubmissionsIntentPath = () => {
    mergeUserInfo({
      planId: 'free',
      free: true,
      onboardingIntent: 'submissions',
      campaignsOnboardingRequired: true,
    });
    window.location.href = `${window.location.origin}/created?session_id=free&intent=submissions`;
  };

  const handleIntentContinue = async (selectedIntent: OnboardingIntent) => {
    setIntentError(null);
    setIntentLoading(true);
    try {
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
      const userId = userInfo?.userId;
      if (!userId) {
        setIntentError('Missing user session. Please log in again.');
        setIntentLoading(false);
        return;
      }

      const response = await axios.post(
        `${BASE_URL}/auth0/user/${encodeURIComponent(userId)}/onboarding-intent`,
        { intent: selectedIntent }
      );

      if (!response?.data?.success) {
        setIntentError(response?.data?.message || 'Failed to save your choice. Please try again.');
        setIntentLoading(false);
        return;
      }

      const meta = response?.data?.data?.user_metadata || {};
      // A/C must always keep campaigns onboarding required until offer complete.
      // Do not trust a false default from an empty/partial metadata response.
      const requiredFromIntent =
        selectedIntent === 'submissions' || selectedIntent === 'both'
          ? true
          : Boolean(meta.campaignsOnboardingRequired);
      mergeUserInfo({
        onboardingIntent: meta.onboardingIntent || selectedIntent,
        campaignsOnboardingRequired: requiredFromIntent,
        free: meta.free === true ? true : userInfo?.free,
        planId: meta.free === true ? 'free' : userInfo?.planId,
      });

      setIntentModalIsOpen(false);

      if (selectedIntent === 'submissions') {
        // Auto free + campaigns path
        completeSubmissionsIntentPath();
        return;
      }

      // B or C → pricing modal (same as today)
      setPrincingModalIsOpen(true);
      setIntentLoading(false);
    } catch (err: any) {
      const status = err?.response?.status;
      const apiMessage = err?.response?.data?.message;
      if (status === 409) {
        // Intent already locked — resume from server state
        const meta = err?.response?.data?.data?.user_metadata;
        const locked = normalizeOnboardingIntent(meta?.onboardingIntent);
        if (locked === 'submissions') {
          completeSubmissionsIntentPath();
          return;
        }
        if (locked === 'tools' || locked === 'both') {
          setIntentModalIsOpen(false);
          setPrincingModalIsOpen(true);
          setIntentLoading(false);
          return;
        }
      }
      setIntentError(apiMessage || 'Failed to save your choice. Please try again.');
      setIntentLoading(false);
    }
  };

  const PricingNext = (id: string, type: string, duration: string, currency: string) => {
    setPrincingModalIsOpen(false);
    if (type === 'Free') {
      let userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
      // Keep A/C campaigns onboarding required while incomplete (do not drop the flag)
      const intent = normalizeOnboardingIntent(userInfo?.onboardingIntent);
      const keepCampaignsRequired =
        userInfo?.campaignsOnboardingRequired === true ||
        intent === 'submissions' ||
        intent === 'both';
      let updatedUser = {
        ...userInfo,
        planId: 'free',
        free: true,
        duration: duration,
        ...(keepCampaignsRequired ? { campaignsOnboardingRequired: true } : {}),
      };
      localStorage.setItem('userInfo', JSON.stringify(updatedUser));
      const intentQuery = intent === 'submissions' || intent === 'both' ? `&intent=${intent}` : '';
      window.location.href = `${window.location.origin}/created?session_id=free${intentQuery}`;
    } else {
      fetch(`${BASE_URL}/stripe-payment-form`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getCuratorAuthHeader(),
        },
        body: JSON.stringify({
          successUrl: `${window.location.origin}/created?session_id={CHECKOUT_SESSION_ID}`,
          cancelUrl: window.location.href,
          email: user?.email,
          subId: user?.userId,
          product_id: id,
          currency: currency.toLowerCase(),
          custId: user?.custId ? `${user?.custId}` : '',
        }),
      })
        .then(res => res.json())
        .then(data => {
          let userInfo = JSON.parse(localStorage.getItem('userInfo'));
          let updatedUser = {
            ...userInfo,
            planId: id,
            free: false,
            duration: duration,
          };
          localStorage.setItem('userInfo', JSON.stringify(updatedUser));
          const checkoutUrl = data?.data?.url;
          if (typeof checkoutUrl !== 'string' || !checkoutUrl) {
            console.error('Failed to create checkout session: missing url', data);
            return;
          }
          window.location.href = checkoutUrl;
        })
        .catch(error => {
          console.error('Failed to create checkout session:', error);
        });
    }
  };

  const handleSkipSubscription = () => {
    setPrincingModalIsOpen(false);
    // Expired subscribers reopening the plan picker should only dismiss the modal.
    if (message === 'expire') {
      return;
    }
    PricingNext(
      process.env.REACT_APP_STRIPE_FREE || '',
      'Free',
      'year',
      defaultCurrency || 'usd'
    );
  };

  const handleUpdatePayment = () => {
    const currentUser = JSON.parse(localStorage.getItem('userInfo') || '{}');
    const customerId = currentUser?.custId || currentUser?.customer;
    if (!customerId) return;
    fetch(`${BASE_URL}/packages/details`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getCuratorAuthHeader() },
      body: JSON.stringify({
        customerId,
        redirectUrl: `${window.location.origin}/shift#access_token=${currentUser?.token || ''}`,
      }),
    })
      .then(res => res.json())
      .then(data => {
        if (data?.data?.url) {
          window.location.href = data.data.url;
        }
      })
      .catch(error => {
        console.error('Failed to open billing portal:', error);
      });
  };

  const [isRetrying, setIsRetrying] = useState<boolean>(false);
  const [retryMessage, setRetryMessage] = useState<string>('');

  const handleRetryPayment = () => {
    const currentUser = JSON.parse(localStorage.getItem('userInfo') || '{}');
    const subId = currentUser?.subId || currentUser?.subscription;
    if (!subId) return;
    setIsRetrying(true);
    setRetryMessage('');
    fetch(`${BASE_URL}/packages/retry`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getCuratorAuthHeader() },
      body: JSON.stringify({ subId }),
    })
      .then(res => res.json())
      .then(data => {
        setIsRetrying(false);
        if (data?.success) {
          setRetryMessage('Payment successful! Redirecting to login...');
          clearCache();
          setTimeout(() => {
            window.location.replace('/home');
          }, 2000);
        } else {
          setRetryMessage(
            data?.message || 'Payment retry failed. Please update your payment method.'
          );
        }
      })
      .catch(error => {
        setIsRetrying(false);
        setRetryMessage('Payment retry failed. Please try updating your payment method.');
        console.error('Failed to retry payment:', error);
      });
  };

  useEffect(() => {
    if (access_token?.access_token) {
      persistAuth0GrantFromHash(access_token as Record<string, unknown>);
      // Get user info from backend
      axios
        .post(`${BASE_URL}/auth0/userinfo`, {
          access_token: access_token?.access_token,
        })
        .then(userInfoResponse => {
          const res = userInfoResponse.data.data;

          axios
            .get(`${BASE_URL}/auth0/user/${res.sub}/profile`)
            .then(profileResponse => profileResponse.data.data)
            .then(async res2 => {
              // Fetch user metadata from our backend API
              let userMetadata: any = {
                free: false,
                customer: null,
                subscription: null,
                payment: null,
                onboardingIntent: null,
                campaignsOnboardingRequired: false,
              };
              try {
                const metadataResponse = await axios.get(
                  `${BASE_URL}/auth0/user/${res.sub}/metadata`
                );
                if (
                  metadataResponse.data?.success &&
                  metadataResponse.data?.data?.user_metadata
                ) {
                  userMetadata = metadataResponse.data.data.user_metadata;
                }
              } catch (metadataError) {
                console.error('Failed to fetch user metadata from backend:', metadataError);
                // Fallback to Auth0 metadata if backend fails
                userMetadata = {
                  free: res2?.user_metadata?.free || false,
                  customer: res2?.user_metadata?.customer || null,
                  subscription: res2?.user_metadata?.subscription || null,
                  payment: res2?.user_metadata?.payment || null,
                  onboardingIntent: res2?.user_metadata?.onboardingIntent || null,
                  campaignsOnboardingRequired:
                    res2?.user_metadata?.campaignsOnboardingRequired || false,
                };
              }

              storeWalletPlan(userMetadata?.walletPlan);

              const displayName = res2?.name || res?.email || '';

              let data = {
                profileImage: res?.picture,
                username: displayName,
                email: res?.email,
                token: access_token?.access_token,
                userId: res.sub,
                allLogin: res2?.logins_count,
                lastLogin: res2?.last_login,
                emailVerified: res2?.email_verified,
                free: userMetadata?.free,
                custId: userMetadata?.customer,
                subId: userMetadata?.subscription,
                onboardingIntent: userMetadata?.onboardingIntent || null,
                campaignsOnboardingRequired: Boolean(userMetadata?.campaignsOnboardingRequired),
                accountType:
                  res2?.identities[0]?.connection === 'Username-Password-Authentication'
                    ? true
                    : false,
              };
              localStorage.setItem('userInfo', JSON.stringify(data));
              if (res?.email) {
                fetch(`${BASE_URL}/packages`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    ...getCuratorAuthHeader(),
                  },
                  body: JSON.stringify({
                    email: `${res.email}`,
                    userId: ides,
                    subId: userMetadata?.subscription,
                    payId: userMetadata?.payment,
                    custId: userMetadata?.customer,
                  }),
                })
                  .then(res => res.json())
                  .then(data => {
                    const packages = Array.isArray(data?.data) ? data.data : [];
                    const paidPackage =
                      packages.find(
                        pkg =>
                          (pkg?.payment_status === 'active' ||
                            pkg?.payment_status === 'trialing') &&
                          (!pkg?.email || pkg.email === res?.email)
                      ) ||
                      packages.find(
                        pkg =>
                          pkg?.payment_status === 'active' ||
                          pkg?.payment_status === 'trialing'
                      );

                    if (paidPackage) {
                      // eslint-disable-next-line react-hooks/exhaustive-deps
                      ides = res.sub;
                      let planId = paidPackage?.planId;
                      let data = {
                        profileImage: res?.picture,
                        username: displayName,
                        email: res?.email,
                        token: access_token?.access_token,
                        userId: res.sub,
                        allLogin: res2?.logins_count,
                        lastLogin: res2?.last_login,
                        emailVerified: res2?.email_verified,
                        planId,
                        free: false,
                        accountType:
                          res2?.identities[0]?.connection ===
                          'Username-Password-Authentication'
                            ? true
                            : false,
                        subId: paidPackage?.subsId,
                        custId: paidPackage?.custId,
                        duration:
                          planId === proPlanMonthly || planId === unlimitedPlanMonthly
                            ? 'month'
                            : planId === proPlan || planId === unlimitedPlan
                              ? 'year'
                              : 'year',
                        onboardingIntent: userMetadata?.onboardingIntent || null,
                        campaignsOnboardingRequired: Boolean(
                          userMetadata?.campaignsOnboardingRequired
                        ),
                      };
                      localStorage.setItem('userInfo', JSON.stringify(data));

                      const config = {
                        headers: {
                          'Content-Type': 'application-json',
                        },
                      };
                      axios
                        .get(`${BASE_URL}/user/${ides}/data`, config)
                        .then(async call => {
                          await hydrateSpotifyStateFromProfiles(call?.data?.data || []);

                          setTimeout(() => {
                            redirectAfterAccessGranted({
                              ...userMetadata,
                              free: false,
                              subscription: paidPackage?.subsId,
                              customer: paidPackage?.custId,
                            });
                          }, 1500);
                        })
                        .catch(err => {
                          handleProfileFetchFailure();
                          setTimeout(() => {
                            redirectAfterAccessGranted({
                              ...userMetadata,
                              free: false,
                              subscription: paidPackage?.subsId,
                              customer: paidPackage?.custId,
                            });
                          }, 1500);
                        });
                    } else if (
                      shouldDowngradeToFreePlan(packages, {
                        subId: userMetadata?.subscription,
                        custId: userMetadata?.customer,
                        free: userMetadata?.free,
                      })
                    ) {
                      // Paid plan has ended (or Stripe no longer returns subs): complete the
                      // same free path as signup so Auth0 metadata and local session stay aligned.
                      clearCache();
                      mergeUserInfo({ planId: 'free', free: true });
                      window.location.replace(
                        `${window.location.origin}${FREE_PLAN_COMPLETION_PATH}`
                      );
                    } else if (packages.length > 0) {
                      setExpireStatus(packages[0]?.payment_status);
                      setShowMessage('expire');
                    } else {
                      // No active package: free landing, intent screen, or pricing
                      handleNoActivePackage(userMetadata);
                    }
                  })
                  .catch(error => {
                    console.error('Network error');
                  });
              }
            })
            .catch(error => {
              if (error) {
                deleteItemFromStorage(SPOTIFY_TOKEN);
                deleteItemFromStorage(SPOTIFY_CODE);
                deleteItemFromStorage(SPOTIFY_USER_INFO);

                // Clear package cache
                clearCache();

                dispatch({
                  type: 'SET_LOGIN',
                  payload: false,
                });
                Auth0Logout();
              }
            });
        })
        .catch(error => {
          if (handleWrongPlatformError(error, setPlatformError)) {
            return;
          }
        });
    }
  }, []);
  const handleLogout = async () => {
    try {
      const { clearAllSpotifyBrowserState } = await import('../../utils/spotifyAuth');
      clearAllSpotifyBrowserState();
    } catch {
      deleteItemFromStorage(SPOTIFY_TOKEN);
      deleteItemFromStorage(SPOTIFY_CODE);
      deleteItemFromStorage(SPOTIFY_USER_INFO);
      localStorage.removeItem('spotifyUser');
    }
    localStorage.removeItem('userInfo');

    // Clear package cache
    clearCache();

    dispatch({
      type: 'SET_LOGIN',
      payload: false,
    });
    Auth0Logout();
  };

  const onBack = async () => {
    try {
      const { clearAllSpotifyBrowserState } = await import('../../utils/spotifyAuth');
      clearAllSpotifyBrowserState();
    } catch {
      deleteItemFromStorage(SPOTIFY_TOKEN);
      deleteItemFromStorage(SPOTIFY_CODE);
      deleteItemFromStorage(SPOTIFY_USER_INFO);
      localStorage.removeItem('spotifyUser');
    }
    localStorage.removeItem('userInfo');

    // Clear package cache
    clearCache();

    Auth0Logout();
  };

  const isPaymentFailed = expireStatus === 'past_due' || expireStatus === 'unpaid';
  const isRetrySuccess = retryMessage.toLowerCase().includes('success');

  return (
    <>
      {message === 'expire' ? (
        <ExpirePageStyle>
          <div className="expire-card">
            <div className={`icon-wrap${isPaymentFailed ? ' failed' : ' expired'}`}>
              <Alert />
            </div>
            <h3 className="expire-title">
              {isPaymentFailed ? 'Payment Failed' : 'Your Subscription has been expired'}
            </h3>
            <p className="expire-desc">
              {isPaymentFailed
                ? 'Your payment method could not be charged. Retry the payment or update your card to regain access to Songplace.'
                : 'Subscribe now to enjoy your favourite playlists.'}
            </p>
            {retryMessage && (
              <p className={`retry-msg${isRetrySuccess ? ' success' : ' error'}`}>{retryMessage}</p>
            )}
            <div className="btn-group">
              {isPaymentFailed && (
                <button onClick={handleRetryPayment} className="btn-primary" disabled={isRetrying}>
                  {isRetrying ? 'Retrying...' : 'Retry Payment'}
                </button>
              )}
              {isPaymentFailed && (
                <button onClick={handleUpdatePayment} className="btn-secondary">
                  Update Payment Method
                </button>
              )}
              <button
                onClick={() => setPrincingModalIsOpen(true)}
                className={isPaymentFailed ? 'btn-tertiary' : 'btn-primary'}
              >
                {isPaymentFailed ? 'Choose a New Plan' : 'Subscribe Now'}
              </button>
              <button onClick={onBack} className="btn-logout">
                Logout
              </button>
            </div>
          </div>
        </ExpirePageStyle>
      ) : (
        <>
          <LoaderPageStyle>
            <div className="loadingPage">
              <h1>{platformError ? 'Unable to continue' : 'Taking you to Songplace'}</h1>
              {platformError ? (
                <p className="platform-error">{platformError}</p>
              ) : (
                <div className="loadingDiv">
                  <div className="loadingCircle"></div>
                </div>
              )}
            </div>
          </LoaderPageStyle>
        </>
      )}

      <OnboardingIntentModal
        isOpen={intentModalIsOpen}
        loading={intentLoading}
        error={intentError}
        onContinue={handleIntentContinue}
        onLogout={handleLogout}
      />

      <PricingModal
        next={PricingNext}
        isOpen={princingModalIsOpen}
        onClose={handleSkipSubscription}
        showmenu={true}
        currentPlan={''}
      />
    </>
  );
};

export default ShiftPage;
